import {
  RECENT_GROUP_LIMIT,
  readRecentGroupUuids,
} from "@/lib/recent-group-uuids";

type ShareWorkspaceBase = {
  groupUuid: string;
  label: string;
  searchText: string;
};

export type ShareWorkspace = ShareWorkspaceBase &
  (
    | { kind: "team"; name: string }
    | { kind: "person"; name: string; email: string }
  );

type ShareTeam = {
  uuid: string;
  name: string;
};

type SharePerson = {
  groupUuid: string;
  name: string;
  email: string;
};

const byLabel = (left: ShareWorkspace, right: ShareWorkspace) =>
  left.label.localeCompare(right.label, undefined, { sensitivity: "base" }) ||
  left.groupUuid.localeCompare(right.groupUuid);

const matchesQuery = (workspace: ShareWorkspace, searchQuery: string) => {
  const normalizedQuery = searchQuery.trim().toLowerCase();
  if (!normalizedQuery) return true;
  return workspace.searchText.toLowerCase().includes(normalizedQuery);
};

export const toShareWorkspaces = ({
  teams,
  people,
  currentGroupUuid,
}: {
  teams: ShareTeam[];
  people: SharePerson[];
  currentGroupUuid: string;
}): ShareWorkspace[] => {
  const teamWorkspaces = teams
    .filter((team) => team.uuid !== currentGroupUuid)
    .map((team) => ({
      kind: "team" as const,
      groupUuid: team.uuid,
      name: team.name,
      label: team.name,
      searchText: team.name,
    }));

  const peopleWorkspaces = people
    .filter((person) => person.groupUuid !== currentGroupUuid)
    .map((person) => ({
      kind: "person" as const,
      groupUuid: person.groupUuid,
      name: person.name,
      email: person.email,
      label: person.name || person.email,
      searchText: `${person.name} ${person.email}`,
    }));

  return [...teamWorkspaces, ...peopleWorkspaces];
};

export const partitionShareWorkspaces = ({
  workspaces,
  grantedGroupUuids,
  recentGroupUuids,
  searchQuery,
}: {
  workspaces: ShareWorkspace[];
  grantedGroupUuids: string[];
  recentGroupUuids: string[];
  searchQuery: string;
}): {
  selected: ShareWorkspace[];
  recent: ShareWorkspace[];
  unselected: ShareWorkspace[];
} => {
  const granted = new Set(grantedGroupUuids);
  const byUuid = new Map(
    workspaces.map((workspace) => [workspace.groupUuid, workspace]),
  );
  const recentIds = readRecentGroupUuids(recentGroupUuids)
    .filter((groupUuid) => byUuid.has(groupUuid))
    .slice(0, RECENT_GROUP_LIMIT);
  const recentIdsSet = new Set(recentIds);

  const selected = workspaces
    .filter(
      (workspace) =>
        granted.has(workspace.groupUuid) &&
        matchesQuery(workspace, searchQuery),
    )
    .sort(byLabel);
  const recent = recentIds
    .map((groupUuid) => byUuid.get(groupUuid)!)
    .filter(
      (workspace) =>
        !granted.has(workspace.groupUuid) &&
        matchesQuery(workspace, searchQuery),
    )
    .sort(byLabel);
  const unselected = workspaces
    .filter(
      (workspace) =>
        !granted.has(workspace.groupUuid) &&
        !recentIdsSet.has(workspace.groupUuid) &&
        matchesQuery(workspace, searchQuery),
    )
    .sort(byLabel);

  return { selected, recent, unselected };
};
